// Admin Cmd+K command palette.
//
// Reuses the @bloom/ui SearchCommand primitive (same surface the customer
// site uses for series search) and adds three groups — Series, Episodes,
// Users — plus a "🪄 NL" badge + parsed-intent chip when the query is
// long/question-shaped enough that the API ran it through Gemma.
//
// Opened via window-level Cmd/Ctrl+K (the global listener lives in the
// admin shell). Selecting a result navigates to its href via react-router.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { SearchCommand, CommandGroup, CommandItem, CommandEmpty, Badge } from "@bloom/ui";
import { Film, ListVideo, User, Sparkles } from "lucide-react";
import { api } from "../api/client.ts";

interface ResultItem {
  type: "series" | "episode" | "user";
  id: string;
  label: string;
  sublabel?: string;
  href: string;
}

interface SearchResponse {
  items: ResultItem[];
  parsed?: {
    type?: "series" | "episode" | "user" | null;
    filters?: Record<string, unknown>;
    query?: string;
  };
  nl?: boolean;
  nlFallback?: boolean;
}

const DEBOUNCE_MS = 250;

export function AdminCmdK({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<ResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState<SearchResponse["parsed"]>();
  const [isNl, setIsNl] = useState(false);
  const [nlFallback, setNlFallback] = useState(false);
  const navigate = useNavigate();

  // Reset transient state every time the palette is reopened so a stale
  // query / hits list from last time doesn't briefly flash.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setItems([]);
    setParsed(undefined);
    setIsNl(false);
    setNlFallback(false);
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    if (!open || !term) {
      setItems([]);
      setLoading(false);
      setParsed(undefined);
      setIsNl(false);
      setNlFallback(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api.post<SearchResponse>("/api/admin/search", {
          q: term,
          limit: 12,
        });
        setItems(data.items);
        setParsed(data.parsed);
        setIsNl(!!data.nl);
        setNlFallback(!!data.nlFallback);
      } catch {
        setItems([]);
        setParsed(undefined);
        setIsNl(false);
        setNlFallback(false);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, open]);

  const grouped = useMemo(() => {
    const series: ResultItem[] = [];
    const episodes: ResultItem[] = [];
    const users: ResultItem[] = [];
    for (const item of items) {
      if (item.type === "series") series.push(item);
      else if (item.type === "episode") episodes.push(item);
      else users.push(item);
    }
    return { series, episodes, users };
  }, [items]);

  const selectItem = (item: ResultItem) => {
    onOpenChange(false);
    navigate(item.href);
  };

  return (
    <SearchCommand
      open={open}
      onOpenChange={onOpenChange}
      placeholder="Search series, episodes, users… or ask in plain English"
      value={query}
      onValueChange={setQuery}
    >
      {/* Status strip — only visible once a query is in flight or completed.
          Keeps the palette quiet on first open. */}
      {query.trim() ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--brand-border)] px-3 py-2 text-xs text-[var(--brand-muted)]">
          {isNl && !nlFallback ? (
            <Badge className="gap-1 bg-[var(--brand-pink-soft)] text-[var(--brand-ink)]">
              <Sparkles className="h-3 w-3" aria-hidden /> NL
            </Badge>
          ) : null}
          {nlFallback ? (
            <span className="text-amber-700">
              Natural-language search unavailable — showing exact matches.
            </span>
          ) : null}
          {parsed && !nlFallback ? <ParsedChip parsed={parsed} /> : null}
          {loading ? <span className="ml-auto">searching…</span> : null}
        </div>
      ) : null}

      {query.trim() && !loading && items.length === 0 ? (
        <CommandEmpty>No matches across series, episodes, or users.</CommandEmpty>
      ) : null}

      {grouped.series.length > 0 ? (
        <CommandGroup heading="Series">
          {grouped.series.map((item) => (
            <CommandItem
              key={`series-${item.id}`}
              value={`series ${item.label} ${item.sublabel ?? ""}`}
              onSelect={() => selectItem(item)}
            >
              <Film className="h-4 w-4 text-[var(--brand-muted)]" aria-hidden />
              <span className="font-medium">{item.label}</span>
              {item.sublabel ? (
                <span className="text-xs text-[var(--brand-muted)]">{item.sublabel}</span>
              ) : null}
            </CommandItem>
          ))}
        </CommandGroup>
      ) : null}

      {grouped.episodes.length > 0 ? (
        <CommandGroup heading="Episodes">
          {grouped.episodes.map((item) => (
            <CommandItem
              key={`episode-${item.id}`}
              value={`episode ${item.label} ${item.sublabel ?? ""}`}
              onSelect={() => selectItem(item)}
            >
              <ListVideo className="h-4 w-4 text-[var(--brand-muted)]" aria-hidden />
              <span className="font-medium">{item.label}</span>
              {item.sublabel ? (
                <span className="text-xs text-[var(--brand-muted)]">{item.sublabel}</span>
              ) : null}
            </CommandItem>
          ))}
        </CommandGroup>
      ) : null}

      {grouped.users.length > 0 ? (
        <CommandGroup heading="Users">
          {grouped.users.map((item) => (
            <CommandItem
              key={`user-${item.id}`}
              value={`user ${item.label} ${item.sublabel ?? ""}`}
              onSelect={() => selectItem(item)}
            >
              <User className="h-4 w-4 text-[var(--brand-muted)]" aria-hidden />
              <span className="font-medium">{item.label}</span>
              {item.sublabel ? (
                <span className="text-xs text-[var(--brand-muted)]">{item.sublabel}</span>
              ) : null}
            </CommandItem>
          ))}
        </CommandGroup>
      ) : null}
    </SearchCommand>
  );
}

// Compact summary of the parsed NL intent — shown under the input so the
// admin can see exactly which filters Gemma inferred.
function ParsedChip({ parsed }: { parsed: NonNullable<SearchResponse["parsed"]> }) {
  const bits: string[] = [];
  if (parsed.type) bits.push(`type=${parsed.type}`);
  const f = parsed.filters ?? {};
  for (const [key, raw] of Object.entries(f)) {
    if (raw === undefined || raw === null || raw === "") continue;
    const value = Array.isArray(raw)
      ? raw.join(",")
      : raw instanceof Date
        ? raw.toISOString().slice(0, 10)
        : typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw)
          ? raw.slice(0, 10)
          : String(raw);
    bits.push(`${key}=${value}`);
  }
  if (bits.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      <span className="text-[var(--brand-muted)]">Filtering:</span>
      {bits.map((bit) => (
        <span
          key={bit}
          className="rounded-full bg-[var(--brand-pink-soft)] px-2 py-0.5 font-mono text-[10px] text-[var(--brand-ink)]"
        >
          {bit}
        </span>
      ))}
    </span>
  );
}
